import { IHarborData } from './harbor-management.types';
import { PriceUnit } from './addon-management.types';
import { BerthType, RefundMode } from '@/constant';
import { UnavailabilityType } from './berth-management.types';

export enum Unit {
  METERS = 'm',
  FEET = 'ft',
}

export enum BookingStatus {
  PENDING = 1,
  CONFIRMED = 2,
  CANCELLED = 3,
  COMPLETED = 4,
  PENDING_CONFIRMATION = 5,
  REJECTED = 6,
}

export interface User {
  id: string;
  name: string | null;
  email: string;
}

export interface Berth {
  id: string;
  berth_number: string;
}

export interface Boat {
  id: string;
  name: string;
  type: string | null;
  length: string | null;
  width: string | null;
  draft: string | null;
  unit: Unit | null;
  registration_number: string | null;
}

export interface BoatDetails {
  length: string | null;
  width: string | null;
  draft: string | null;
  unit: Unit | null;
  boat_registration_number: string | null;
  weight?: string | null;
}

export interface GuestDetails {
  first_name?: string;
  last_name?: string;
  email?: string;
  mobile?: string;
  country_code?: string;
  billing_address_line_1?: string;
  city?: string;
  post_code?: string;
}

export interface Booking {
  id: string;
  user: User;
  berth: Berth;
  start_date: string;
  end_date: string;
  harbor: IHarborData;
  total_price: number;
  payment_type: number;
  status: number;
  special_requests: string | null;
  cancellation_reason: string | null;
  booking_reference?: string | null;
  is_guest_booking?: boolean;
  is_claimed?: boolean;
  claimed_at?: string | null;
  guest_details?: GuestDetails | null;
  // Present when backend sends conflict info
  isConflicting?: boolean;
  mediation_case?: {
    id: string;
    status: string;
    response_deadline_at?: string | null;
  };
  payment_transactions: Array<{
    payment_intent_id: string | null;
    paytrail_payment_id: string | null;
    stripe_refund_id: string | null;
    id: string;
    amount: number;
    status: string;
    currency: string;
    created_at: string;
    refunded_amount: number;
    refunded_at: string;
    type: string;
    updated_at: string;
  }>;
  booking_addons: Array<{
    id: string;
    quantity: number;
    price_at_booking: number;
    price_unit_at_booking: PriceUnit;
    addon: {
      id: string;
      name: string;
    };
    no_of_slots: number;
    start_date?: string;
    end_date?: string;
  }>;
  created_at: string;
  boat: Boat | null;
  boat_details: BoatDetails | null;
  captain_family_name?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  quantity?: number;
  service?: {
    id: string;
    name: string;
    service_provider_id?: string;
    owner_type?: string;
    owner_id?: string;
    description?: string;
    category?: string;
    sub_category?: string;
    service_type?: string;
    price_amount?: string;
    booking_slot_type?: string;
    latitude?: string | number | null;
    longitude?: string | number | null;
    distance?: string | number | null;
    distance_unit?: string | null;
  };
}

export interface BookingNoShowSummary {
  booking_id: string;
  mode: RefundMode;
  net_paid: number;
  booking_total_price: number;
  penalty_base: number;
  penalty_amount: number;
  total_refund: number;
  currency: string;
}

export interface IBookingData {
  id: string;
  user: {
    id: string;
    name: string | null;
    email: string;
  };
  berth: {
    id: string;
    berth_number: string;
  };
  service?: {
    id: string;
    name: string;
  };
  start_date: string;
  end_date: string;
  total_price: number;
  payment_type: number;
  status: number;
  special_requests: string | null;
  cancellation_reason: string | null;
  booking_reference?: string | null;
  is_guest_booking?: boolean;
  is_claimed?: boolean;
  claimed_at?: string | null;
  guest_details?: GuestDetails | null;
  booking_addons: Array<{
    id: string;
    quantity: number;
    price_at_booking: number;
    price_unit_at_booking: PriceUnit;
    addon: {
      id: string;
      name: string;
    };
    no_of_slots: number;
    start_date?: string;
    end_date?: string;
  }>;
  created_at: string;
  boat: Boat | null;
  boat_details: BoatDetails | null;
  captain_family_name?: string | null;
  isConflicting?: boolean;
  mediation_case?: {
    id: string;
    status: string;
    response_deadline_at?: string | null;
  } | null;
}

export interface PaginatedBookingResponse {
  data?: Booking[];
  items?: Booking[];
  total: number;
  page?: number;
  limit?: number;
  totalPages?: number;
}

export interface UnavailabilityItem {
  start_date: string;
  end_date?: string;
  id?: string;
  berth_id?: string;
  reason?: string;
  created_at?: string;
  type?: UnavailabilityType;
  external_reference?: string | null;
  created_by?: { id: string; name?: string | null; email?: string } | null;
  updated_by?: { id: string; name?: string | null; email?: string } | null;
  updated_at?: string | null;
}

export interface AvailableBerth {
  id: string;
  berth_number: string;
  category_id: string;
  harbor_id: string;
  width: number;
  length: number;
  depth: number;
  is_available: boolean;
  berth_type?: BerthType;
  category?: {
    id: string;
    name: string;
  };
  pier?: {
    id: string;
    name: string;
    boater_label?: string;
    capacity_mode?: 'ENUMERATED' | 'POOLED' | 'SINGLE_BERTH_POOL';
    pooled_capacity?: number;
  };
  harbor?: {
    id?: string;
    name?: string;
    location?: string;
  } & Pick<IHarborData, 'seasonality_support' | 'seasonal_dates'>;
  unavailabilities?: UnavailabilityItem[];
}
